import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useGetBankList } from './useGetBankList';
import { useSaveBankAccountInfo } from './useSaveBankAccount';
import { useGetBankAccountInfo } from './useGetBankAccountInfo';
import { FORM_MODES } from '../domain';
import { IS_BR_CONTEXT } from '@/config/consts/commons';

const EMPTY_ACCOUNTS = {};

export const useBankAccountForm = ({ countryConfig, userType = 'clouder' }) => {
  const authState = useSelector((state) => state.auth);
  const { idClouder } = useParams();

  const userId = idClouder || authState.username;

  const { data: banksData, isFetching, isLoading } = useGetBankList();

  const { data: existingBankData, isLoading: isLoadingExistingData } =
    useGetBankAccountInfo({ userId, userType });

  const { mutate: saveBankAccountMutate, isPending: isSavingData } =
    useSaveBankAccountInfo({ userId, userType });

  const isLoadingDataBankList = isFetching || isLoading;

  const accounts = IS_BR_CONTEXT
    ? existingBankData?.data?.accounts ?? EMPTY_ACCOUNTS
    : EMPTY_ACCOUNTS;
  const requiresCnpj = IS_BR_CONTEXT
    ? !!existingBankData?.data?.requires_cnpj
    : false;
  const hasExistingClBankAccount = !IS_BR_CONTEXT
    ? Boolean(
        existingBankData?.data?.accounts?.RUT?.id_user_bank_account ||
          existingBankData?.data?.id_user_bank_account
      )
    : false;

  const [createdAccountTypes, setCreatedAccountTypes] = useState(new Set());
  const [hasCreatedClBankAccount, setHasCreatedClBankAccount] = useState(false);

  const onSubmit = useCallback(
    (data) => {
      if (!userId) {
        throw new Error('User ID is required for saving bank account data');
      }

      const payload = countryConfig.mapperToBankAccountPayload(data);

      if (IS_BR_CONTEXT && data.formMode) {
        const activeKey = data.formMode === FORM_MODES.CPF ? 'CPF' : 'CNPJ';
        const isUpdate =
          !!accounts[activeKey] || createdAccountTypes.has(activeKey);

        saveBankAccountMutate(
          { payload, isUpdate },
          {
            onSuccess: () => {
              if (!isUpdate) {
                setCreatedAccountTypes((prev) => new Set(prev).add(activeKey));
              }
            },
          }
        );
      } else {
        const isUpdate = hasExistingClBankAccount || hasCreatedClBankAccount;

        saveBankAccountMutate(
          { payload, isUpdate },
          {
            onSuccess: () => {
              if (!isUpdate) {
                setHasCreatedClBankAccount(true);
              }
            },
          }
        );
      }
    },
    [
      userId,
      countryConfig,
      accounts,
      createdAccountTypes,
      hasExistingClBankAccount,
      hasCreatedClBankAccount,
      saveBankAccountMutate,
    ]
  );

  return {
    banksData,
    existingBankData,
    accounts,
    requiresCnpj,
    isLoadingDataBankList,
    isSavingData,
    isLoadingExistingData,
    onSubmit,
  };
};
